import { z } from 'zod';
import { DurationScale } from './enums';
import { coerceNumber } from '../validation';

// Upper bound of a duration's value per scale (months input: 1..36 — a
// blank input carries 0 and is dropped by the settings API before
// validation).
export const durationScaleMaxValue: Record<DurationScale, number> = {
  [DurationScale.month]: 36,
};

// A stored duration, kept structured (never baked into identifiers or
// display strings) so values stay comparable and new amounts/scales need no
// schema change.
export const DurationSchema = z
  .object({
    value: coerceNumber(z.number().int().min(1)),
    scale: z.enum(DurationScale),
  })
  // an invalid scale is rejected by the enum above — this only bounds a value
  // whose scale is known, and reports it on the value that is out of range
  .check((ctx) => {
    const { value, scale } = ctx.value;
    const max = durationScaleMaxValue[scale];

    if (max === undefined || value === undefined || value <= max) {
      return;
    }

    ctx.issues.push({
      code: 'custom',
      input: ctx.value,
      path: ['value'],
      message: `Duration must be between 1 and ${max} ${scale}s`,
    });
  });

export type Duration = z.infer<typeof DurationSchema>;

export function displayDuration({ value, scale }: Duration) {
  return `${value} ${scale}${value === 1 ? '' : 's'}`;
}

// The date `duration` after `date`, on the calendar (a month after 31 Jan
// rolls over into March, as Date.setMonth does).
export function addDuration(date: Date, { value, scale }: Duration) {
  const result = new Date(date);
  switch (scale) {
    case DurationScale.month:
      result.setMonth(result.getMonth() + value);
      return result;
    default:
      throw new Error(`Unknown duration scale: ${scale}`);
  }
}

// Approximate scale sizes in days — ONLY for ordering durations, never for
// date arithmetic.
const durationScaleDays: Record<DurationScale, number> = {
  [DurationScale.month]: 30,
};

export function compareDurations(a: Duration, b: Duration) {
  return (
    a.value * durationScaleDays[a.scale] - b.value * durationScaleDays[b.scale]
  );
}
